import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  PlusIcon,
  PencilIcon,
  TrashIcon,
  MagnifyingGlassIcon,
  ArrowLeftIcon,
  MapPinIcon,
  UserPlusIcon,
  ArrowPathIcon
} from '@heroicons/react/24/outline';
import { mealDeskVendorsService, citiesService } from '../firebase/services';
import { 
  collection, 
  query,
  orderBy,
  onSnapshot
} from 'firebase/firestore';
import { db } from '../firebase/config';
import { mealDeskCompaniesService } from '../firebase/services';
import VendorOnboardingForm from './VendorOnboardingForm';
import VendorDocumentCollection from './VendorDocumentCollection';

const MealDeskVendorsManager = () => {
  const navigate = useNavigate();
  const [vendors, setVendors] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDrawer, setShowDrawer] = useState(false);
  const [editingVendor, setEditingVendor] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterCuisine, setFilterCuisine] = useState('');
  const [showOnboardingForm, setShowOnboardingForm] = useState(false);
  const [showDocumentCollection, setShowDocumentCollection] = useState(false);
  const [selectedVendorForDocs, setSelectedVendorForDocs] = useState(null);
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  
  // States and Cities data
  const [states, setStates] = useState([]);
  const [cities, setCities] = useState([]);
  const [availableCities, setAvailableCities] = useState([]);
  
  const [formData, setFormData] = useState({
    name: '',
    firstName: '',
    lastName: '',
    phone: '',
    contactEmail: '',
    addressLine1: '',
    addressLine2: '',
    state: '',
    city: '',
    pincode: '',
    licenseNumber: '',
    companyLegalStructure: '',
    hasBusinessInsurance: '',
    cuisines: [],
    serviceHours: {
      breakfast: false,
      lunch: false,
      dinner: false
    },
    deliveryModes: [],
    locationsServed: [],
    rating: 0
  });
  const [errors, setErrors] = useState({});

  // Predefined options
  const cuisineOptions = [
    'Indian', 'Chinese', 'Italian', 'Mexican', 'Thai', 'Japanese', 'Korean', 
    'Mediterranean', 'American', 'French', 'Middle Eastern', 'Vietnamese', 
    'Greek', 'Spanish', 'Lebanese', 'Turkish', 'Fusion', 'Vegetarian', 'Vegan'
  ];

  const deliveryModeOptions = [
    'Delivery', 'Pickup', 'Dine-in', 'Drive-through', 'Catering', 'Food Truck'
  ];

  const serviceHoursOptions = [
    { key: 'breakfast', label: 'Breakfast' },
    { key: 'lunch', label: 'Lunch' },
    { key: 'dinner', label: 'Dinner' }
  ];

  const companyLegalStructureOptions = [
    'Private Limited Company',
    'Limited Liability Partnership (LLP)',
    'Sole Proprietorship',
    'One Person Company (OPC)',
    'Partnership Firm',
    'Public Limited Company',
    'Section 8 Company',
    'Not Registered Business'
  ];

  const businessInsuranceOptions = [
    { value: 'yes', label: 'Yes' },
    { value: 'no', label: 'No' }
  ];

  // Load vendors and companies
  useEffect(() => {
    const loadData = async () => {
      try {
        console.log('Loading MealDesk vendors...');
        
        // Load vendors with real-time updates
        const vendorsQuery = query(collection(db, 'MealDeskVendors'));
        console.log('Vendors query created:', vendorsQuery);
        
        const unsubscribeVendors = onSnapshot(vendorsQuery, (snapshot) => {
          console.log('Vendors snapshot received:', snapshot.docs.length, 'documents');
          const vendorsData = snapshot.docs.map(doc => {
            const data = {
              id: doc.id,
              ...doc.data()
            };
            console.log('Vendor data:', data);
            return data;
          });
          console.log('Setting vendors state with:', vendorsData.length, 'vendors');
          console.log('All vendor IDs:', vendorsData.map(v => v.id));
          setVendors(vendorsData);
          setLoading(false);
        }, (error) => {
          console.error('Error in vendors snapshot:', error);
          setLoading(false);
        });

        // Load companies
        console.log('Loading companies...');
        const companiesData = await mealDeskCompaniesService.getAllCompanies();
        console.log('Companies loaded:', companiesData.length);
        setCompanies(companiesData);

        // Load states and cities
        console.log('Loading states and cities...');
        const uniqueStates = await citiesService.getUniqueStates();
        setStates(uniqueStates);

        const allCities = await citiesService.getAllCities();
        setCities(allCities);

        return () => {
          console.log('Cleaning up vendors listener');
          unsubscribeVendors();
        };
      } catch (error) {
        console.error('Error loading data:', error);
        setLoading(false);
      }
    };

    loadData();
  }, [refreshTrigger]);

  // Update available cities when state changes
  useEffect(() => {
    const updateAvailableCities = () => {
      if (formData.state) {
        const filteredCities = cities
          .filter(city => city.State === formData.state)
          .map(city => city.City)
          .sort();
        setAvailableCities(filteredCities);
      } else {
        setAvailableCities([]);
      }
    };

    updateAvailableCities();
  }, [formData.state, cities]);

  // Filter vendors based on search and filters
  const filteredVendors = vendors.filter(vendor => {
    const contactName = vendor.firstName && vendor.lastName 
      ? `${vendor.firstName} ${vendor.lastName}`
      : vendor.contactName || `${vendor.firstName || ''} ${vendor.lastName || ''}`.trim();
    
    const matchesSearch = vendor.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         contactName.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         vendor.contactEmail?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         vendor.firstName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         vendor.lastName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         vendor.city?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         vendor.state?.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesCuisine = !filterCuisine || vendor.cuisines?.includes(filterCuisine);
    
    return matchesSearch && matchesCuisine;
  });

  // Reset form
  const resetForm = () => {
    setFormData({
      name: '',
      firstName: '',
      lastName: '',
      phone: '',
      contactEmail: '',
      addressLine1: '',
      addressLine2: '',
      state: '',
      city: '',
      pincode: '',
      licenseNumber: '',
      companyLegalStructure: '',
      hasBusinessInsurance: '',
      cuisines: [],
      serviceHours: {
        breakfast: false,
        lunch: false,
        dinner: false
      },
      deliveryModes: [],
      locationsServed: [],
      rating: 0
    });
    setErrors({});
    setEditingVendor(null);
  };

  // Open drawer for add/edit
  const openDrawer = (vendor = null) => {
    if (vendor) {
      setFormData({
        name: vendor.name || '',
        firstName: vendor.firstName || '',
        lastName: vendor.lastName || '',
        phone: vendor.phone || '',
        contactEmail: vendor.contactEmail || '',
        addressLine1: vendor.addressLine1 || '',
        addressLine2: vendor.addressLine2 || '',
        state: vendor.state || '',
        city: vendor.city || '',
        pincode: vendor.pincode || '',
        licenseNumber: vendor.licenseNumber || '',
        companyLegalStructure: vendor.companyLegalStructure || '',
        hasBusinessInsurance: vendor.hasBusinessInsurance || '',
        cuisines: vendor.cuisines || [],
        serviceHours: vendor.serviceHours || {
          breakfast: false,
          lunch: false,
          dinner: false
        },
        deliveryModes: vendor.deliveryModes || [],
        locationsServed: vendor.locationsServed || [],
        rating: vendor.rating || 0
      });
      setEditingVendor(vendor);
    } else {
      resetForm();
    }
    setShowDrawer(true);
  };

  // Close drawer
  const closeDrawer = () => {
    setShowDrawer(false);
    resetForm();
  };

  // Handle form input changes
  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    
    // Clear error for this field
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  };

  // Handle state change and clear city
  const handleStateChange = (state) => {
    setFormData(prev => ({
      ...prev,
      state: state,
      city: ''
    }));
  };

  // Handle cuisines selection
  const handleCuisinesChange = (cuisine) => {
    setFormData(prev => ({
      ...prev,
      cuisines: prev.cuisines.includes(cuisine)
        ? prev.cuisines.filter(c => c !== cuisine)
        : [...prev.cuisines, cuisine]
    }));
  };

  // Handle service hours change
  const handleServiceHoursChange = (meal) => {
    setFormData(prev => ({
      ...prev,
      serviceHours: {
        ...prev.serviceHours,
        [meal]: !prev.serviceHours[meal]
      }
    }));
  };

  // Handle delivery modes change
  const handleDeliveryModesChange = (mode) => {
    setFormData(prev => ({
      ...prev,
      deliveryModes: prev.deliveryModes.includes(mode)
        ? prev.deliveryModes.filter(m => m !== mode)
        : [...prev.deliveryModes, mode]
    }));
  };

  // Handle locations served change
  const handleLocationsServedChange = (locationId) => {
    setFormData(prev => ({
      ...prev,
      locationsServed: prev.locationsServed.includes(locationId)
        ? prev.locationsServed.filter(l => l !== locationId)
        : [...prev.locationsServed, locationId]
    }));
  };

  // Validate form
  const validateForm = () => {
    const newErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Vendor name is required';
    }

    if (!formData.firstName.trim()) {
      newErrors.firstName = 'First name is required';
    }

    if (!formData.lastName.trim()) {
      newErrors.lastName = 'Last name is required';
    }

    if (!formData.phone.trim()) {
      newErrors.phone = 'Phone number is required';
    } else if (!/^[0-9+\-\s()]+$/.test(formData.phone)) {
      newErrors.phone = 'Invalid phone number format';
    }

    if (!formData.contactEmail.trim()) {
      newErrors.contactEmail = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.contactEmail)) {
      newErrors.contactEmail = 'Invalid email format';
    }

    if (!formData.addressLine1.trim()) {
      newErrors.addressLine1 = 'Address line 1 is required';
    }

    if (!formData.state) {
      newErrors.state = 'State is required';
    }

    if (!formData.city) {
      newErrors.city = 'City is required';
    }

    if (!formData.pincode.trim()) {
      newErrors.pincode = 'Pincode is required';
    }

    if (!formData.licenseNumber.trim()) {
      newErrors.licenseNumber = 'License number is required';
    }

    if (!formData.companyLegalStructure) {
      newErrors.companyLegalStructure = 'Company legal structure is required';
    }

    if (!formData.hasBusinessInsurance) {
      newErrors.hasBusinessInsurance = 'Business insurance status is required';
    }

    if (formData.cuisines.length === 0) {
      newErrors.cuisines = 'At least one cuisine is required';
    }

    if (formData.deliveryModes.length === 0) {
      newErrors.deliveryModes = 'At least one delivery mode is required';
    }

    if (formData.rating < 0 || formData.rating > 5) {
      newErrors.rating = 'Rating must be between 0 and 5';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Save vendor
  const saveVendor = async () => {
    if (!validateForm()) return;

    try {
      console.log('Saving vendor with data:', formData);
      
      if (editingVendor) {
        console.log('Updating existing vendor:', editingVendor.id);
        await mealDeskVendorsService.updateVendor(editingVendor.id, formData);
        console.log('Vendor updated successfully');
      } else {
        console.log('Adding new vendor...');
        const vendorId = await mealDeskVendorsService.addVendor(formData);
        console.log('Vendor added successfully with ID:', vendorId);
      }

      closeDrawer();
      
      // Show success message
      if (editingVendor) {
        alert(`Vendor "${formData.name}" updated successfully!`);
      } else {
        alert(`Vendor "${formData.name}" added successfully! The vendor has been added to the list but is not yet approved. Use the "Edit Vendor" option to complete the onboarding process with documents and approval.`);
      }
    } catch (error) {
      console.error('Error saving vendor:', error);
      alert('Error saving vendor. Please try again.');
    }
  };

  // Delete vendor
  const deleteVendor = async (vendorId) => {
    if (window.confirm('Are you sure you want to delete this vendor?')) {
      try {
        await mealDeskVendorsService.deleteVendor(vendorId);
      } catch (error) {
        console.error('Error deleting vendor:', error);
        alert('Error deleting vendor. Please try again.');
      }
    }
  };

  // Manual refresh function
  const refreshVendors = () => {
    console.log('Manual refresh triggered');
    setRefreshTrigger(prev => prev + 1);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b border-gray-200 px-6 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <button
              onClick={() => navigate('/')}
              className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <ArrowLeftIcon className="h-5 w-5" />
            </button>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Kerblet MealDesk Vendors</h1>
              <p className="text-sm text-gray-500">Manage meal service vendors</p>
            </div>
          </div>
          <div className="flex space-x-3">
            <button
              onClick={refreshVendors}
              className="inline-flex items-center px-4 py-2 bg-gray-600 text-white text-sm font-medium rounded-lg hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 transition-colors"
              title="Refresh vendor list"
            >
              <ArrowPathIcon className="h-4 w-4 mr-2" />
              Refresh
            </button>
            <button
              onClick={() => setShowOnboardingForm(true)}
              className="inline-flex items-center px-4 py-2 bg-green-600 text-white text-sm font-medium rounded-lg hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 transition-colors"
              title="Complete onboarding with documents and approval"
            >
              <UserPlusIcon className="h-4 w-4 mr-2" />
              Onboard Vendor
            </button>
            <button
              onClick={() => openDrawer()}
              className="inline-flex items-center px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors"
              title="Quick add existing vendor without documents"
            >
              <PlusIcon className="h-4 w-4 mr-2" />
              Quick Add
            </button>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white border-b border-gray-200 px-6 py-4">
        <div className="flex flex-col sm:flex-row gap-4">
          {/* Search */}
          <div className="flex-1">
            <div className="relative">
              <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search vendors..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
          </div>

          {/* Cuisine Filter */}
          <div className="sm:w-48">
            <select
              value={filterCuisine}
              onChange={(e) => setFilterCuisine(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="">All Cuisines</option>
              {cuisineOptions.map((cuisine) => (
                <option key={cuisine} value={cuisine}>
                  {cuisine}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto p-6">
        <div className="bg-white rounded-lg shadow">
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 className="text-lg font-medium text-gray-900">
              Vendors ({filteredVendors.length} of {vendors.length})
            </h2>
          </div>
          
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Vendor
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Contact
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Cuisines
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Service Hours
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Rating
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredVendors.map((vendor) => (
                  <tr key={vendor.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div>
                        <div className="text-sm font-medium text-gray-900">{vendor.name}</div>
                        <div className="text-sm text-gray-500 flex items-center mt-1">
                          <MapPinIcon className="h-4 w-4 mr-1" />
                          {vendor.addressLine1 || vendor.address}
                          {vendor.addressLine2 && <span>, {vendor.addressLine2}</span>}
                          {vendor.city && <span>, {vendor.city}</span>}
                          {vendor.state && <span>, {vendor.state}</span>}
                        </div>
                        <div className="text-xs text-gray-400 mt-1">
                          License: {vendor.licenseNumber}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">
                        {vendor.firstName && vendor.lastName 
                          ? `${vendor.firstName} ${vendor.lastName}`
                          : vendor.contactName || `${vendor.firstName || ''} ${vendor.lastName || ''}`.trim()
                        }
                      </div>
                      <div className="text-sm text-gray-500">{vendor.phone}</div>
                      <div className="text-sm text-gray-500">{vendor.contactEmail}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex flex-wrap gap-1">
                        {vendor.cuisines?.slice(0, 3).map((cuisine) => (
                          <span key={cuisine} className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-blue-100 text-blue-800">
                            {cuisine}
                          </span>
                        ))}
                        {vendor.cuisines?.length > 3 && (
                          <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-gray-100 text-gray-600">
                            +{vendor.cuisines.length - 3}
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex flex-wrap gap-1">
                        {Object.entries(vendor.serviceHours || {}).map(([meal, active]) => (
                          active && (
                            <span key={meal} className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-green-100 text-green-800 capitalize">
                              {meal}
                            </span>
                          )
                        ))}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <span className="text-sm text-gray-900">{vendor.rating || 0}/5</span>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        {vendor.isApproved ? (
                          <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-green-100 text-green-800">
                            Approved
                          </span>
                        ) : vendor.onboardingDocs && Object.keys(vendor.onboardingDocs).length > 0 ? (
                          <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-yellow-100 text-yellow-800">
                            Pending Approval
                          </span>
                        ) : (
                          <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-red-100 text-red-800">
                            No Documents
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <div className="flex space-x-2">
                        <button
                          onClick={() => navigate(`/kerbletmeal/vendors/${vendor.id}/menu`)}
                          className="text-green-600 hover:text-green-900 p-1"
                          title="Manage Menu"
                        >
                          <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                          </svg>
                        </button>
                        {(!vendor.onboardingDocs || Object.keys(vendor.onboardingDocs || {}).length === 0) && (
                          <button
                            onClick={() => {
                              setSelectedVendorForDocs(vendor);
                              setShowDocumentCollection(true);
                            }}
                            className="text-orange-600 hover:text-orange-900 p-1"
                            title="Collect Documents"
                          >
                            <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                            </svg>
                          </button>
                        )}
                        <button
                          onClick={() => {
                            setEditingVendor(vendor);
                            setShowOnboardingForm(true);
                          }}
                          className="text-blue-600 hover:text-blue-900 p-1"
                          title="Edit Vendor"
                        >
                          <PencilIcon className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => deleteVendor(vendor.id)}
                          className="text-red-600 hover:text-red-900 p-1"
                        >
                          <TrashIcon className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Drawer */}
      {showDrawer && (
        <div className="fixed inset-0 z-50 overflow-hidden">
          <div className="absolute inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={closeDrawer}></div>
          
          <div className="fixed inset-y-0 right-0 pl-10 max-w-full flex">
            <div className="w-screen max-w-md">
              <div className="h-full flex flex-col bg-white shadow-xl">
                {/* Drawer header */}
                <div className="px-6 py-4 border-b border-gray-200">
                  <div className="flex items-center justify-between">
                    <h2 className="text-lg font-medium text-gray-900">
                      {editingVendor ? 'Edit Vendor' : 'Quick Add Vendor'}
                    </h2>
                    <button
                      onClick={closeDrawer}
                      className="text-gray-400 hover:text-gray-500"
                    >
                      <span className="sr-only">Close</span>
                      <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </div>
                </div>

                {/* Drawer content */}
                <div className="flex-1 overflow-y-auto px-6 py-4">
                  <form className="space-y-6">
                    {/* Business Name */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700">
                        Business Name *
                      </label>
                      <input
                        type="text"
                        value={formData.name}
                        onChange={(e) => handleInputChange('name', e.target.value)}
                        className={`mt-1 block w-full border rounded-md px-3 py-2 text-sm ${
                          errors.name ? 'border-red-300' : 'border-gray-300'
                        } focus:outline-none focus:ring-blue-500 focus:border-blue-500`}
                        placeholder="Enter business name"
                      />
                      {errors.name && <p className="mt-1 text-sm text-red-600">{errors.name}</p>}
                    </div>

                    {/* First Name */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700">
                        Vendor Contact's First Name *
                      </label>
                      <input
                        type="text"
                        value={formData.firstName}
                        onChange={(e) => handleInputChange('firstName', e.target.value)}
                        className={`mt-1 block w-full border rounded-md px-3 py-2 text-sm ${
                          errors.firstName ? 'border-red-300' : 'border-gray-300'
                        } focus:outline-none focus:ring-blue-500 focus:border-blue-500`}
                        placeholder="Enter vendor contact's first name"
                      />
                      {errors.firstName && <p className="mt-1 text-sm text-red-600">{errors.firstName}</p>}
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">
                        Vendor Contact's Last Name *
                      </label>
                      <input
                        type="text"
                        value={formData.lastName}
                        onChange={(e) => handleInputChange('lastName', e.target.value)}
                        className={`mt-1 block w-full border rounded-md px-3 py-2 text-sm ${
                          errors.lastName ? 'border-red-300' : 'border-gray-300'
                        } focus:outline-none focus:ring-blue-500 focus:border-blue-500`}
                        placeholder="Enter vendor contact's last name"
                      />
                      {errors.lastName && <p className="mt-1 text-sm text-red-600">{errors.lastName}</p>}
                    </div>

                    {/* Phone */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700">
                        Vendor Contact's Phone *
                      </label>
                      <input
                        type="tel"
                        value={formData.phone}
                        onChange={(e) => handleInputChange('phone', e.target.value)}
                        className={`mt-1 block w-full border rounded-md px-3 py-2 text-sm ${
                          errors.phone ? 'border-red-300' : 'border-gray-300'
                        } focus:outline-none focus:ring-blue-500 focus:border-blue-500`}
                        placeholder="Enter vendor contact's phone number"
                      />
                      {errors.phone && <p className="mt-1 text-sm text-red-600">{errors.phone}</p>}
                    </div>

                    {/* Email */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700">
                        Vendor Contact's Email *
                      </label>
                      <input
                        type="email"
                        value={formData.contactEmail}
                        onChange={(e) => handleInputChange('contactEmail', e.target.value)}
                        className={`mt-1 block w-full border rounded-md px-3 py-2 text-sm ${
                          errors.contactEmail ? 'border-red-300' : 'border-gray-300'
                        } focus:outline-none focus:ring-blue-500 focus:border-blue-500`}
                        placeholder="Enter vendor contact's email address"
                      />
                      {errors.contactEmail && <p className="mt-1 text-sm text-red-600">{errors.contactEmail}</p>}
                    </div>

                    {/* Address */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700">
                        Address *
                      </label>
                      <input
                        type="text"
                        value={formData.addressLine1}
                        onChange={(e) => handleInputChange('addressLine1', e.target.value)}
                        className={`mt-1 block w-full border rounded-md px-3 py-2 text-sm ${
                          errors.addressLine1 ? 'border-red-300' : 'border-gray-300'
                        } focus:outline-none focus:ring-blue-500 focus:border-blue-500`}
                        placeholder="Enter address line 1"
                      />
                      {errors.addressLine1 && <p className="mt-1 text-sm text-red-600">{errors.addressLine1}</p>}
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">
                        Address Line 2
                      </label>
                      <input
                        type="text"
                        value={formData.addressLine2}
                        onChange={(e) => handleInputChange('addressLine2', e.target.value)}
                        className={`mt-1 block w-full border rounded-md px-3 py-2 text-sm ${
                          errors.addressLine2 ? 'border-red-300' : 'border-gray-300'
                        } focus:outline-none focus:ring-blue-500 focus:border-blue-500`}
                        placeholder="Enter address line 2"
                      />
                      {errors.addressLine2 && <p className="mt-1 text-sm text-red-600">{errors.addressLine2}</p>}
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">
                        State *
                      </label>
                      <select
                        value={formData.state}
                        onChange={(e) => handleStateChange(e.target.value)}
                        className={`mt-1 block w-full border rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 ${
                          errors.state ? 'border-red-300' : 'border-gray-300'
                        }`}
                      >
                        <option value="">Select State</option>
                        {states.map((state) => (
                          <option key={state} value={state}>
                            {state}
                          </option>
                        ))}
                      </select>
                      {errors.state && <p className="mt-1 text-sm text-red-600">{errors.state}</p>}
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">
                        City *
                      </label>
                      <select
                        value={formData.city}
                        onChange={(e) => handleInputChange('city', e.target.value)}
                        disabled={!formData.state}
                        className={`mt-1 block w-full border rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 ${
                          errors.city ? 'border-red-300' : 'border-gray-300'
                        } ${!formData.state ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                      >
                        <option value="">{formData.state ? 'Select City' : 'Select State First'}</option>
                        {availableCities.map((city) => (
                          <option key={city} value={city}>
                            {city}
                          </option>
                        ))}
                      </select>
                      {errors.city && <p className="mt-1 text-sm text-red-600">{errors.city}</p>}
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">
                        Pincode *
                      </label>
                      <input
                        type="text"
                        value={formData.pincode}
                        onChange={(e) => handleInputChange('pincode', e.target.value)}
                        className={`mt-1 block w-full border rounded-md px-3 py-2 text-sm ${
                          errors.pincode ? 'border-red-300' : 'border-gray-300'
                        } focus:outline-none focus:ring-blue-500 focus:border-blue-500`}
                        placeholder="Enter pincode"
                      />
                      {errors.pincode && <p className="mt-1 text-sm text-red-600">{errors.pincode}</p>}
                    </div>

                    {/* License Number */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700">
                        License Number *
                      </label>
                      <input
                        type="text"
                        value={formData.licenseNumber}
                        onChange={(e) => handleInputChange('licenseNumber', e.target.value)}
                        className={`mt-1 block w-full border rounded-md px-3 py-2 text-sm ${
                          errors.licenseNumber ? 'border-red-300' : 'border-gray-300'
                        } focus:outline-none focus:ring-blue-500 focus:border-blue-500`}
                        placeholder="Enter license number"
                      />
                      {errors.licenseNumber && <p className="mt-1 text-sm text-red-600">{errors.licenseNumber}</p>}
                    </div>

                    {/* Cuisines */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700">
                        Cuisines *
                      </label>
                      <div className="mt-2 max-h-32 overflow-y-auto border border-gray-300 rounded-md p-2">
                        <div className="grid grid-cols-2 gap-2">
                          {cuisineOptions.map((cuisine) => (
                            <label key={cuisine} className="flex items-center">
                              <input
                                type="checkbox"
                                checked={formData.cuisines.includes(cuisine)}
                                onChange={() => handleCuisinesChange(cuisine)}
                                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                              />
                              <span className="ml-2 text-sm text-gray-700">{cuisine}</span>
                            </label>
                          ))}
                        </div>
                      </div>
                      {errors.cuisines && <p className="mt-1 text-sm text-red-600">{errors.cuisines}</p>}
                    </div>

                    {/* Service Hours */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700">
                        Service Hours
                      </label>
                      <div className="mt-2 space-y-2">
                        {serviceHoursOptions.map((option) => (
                          <label key={option.key} className="flex items-center">
                            <input
                              type="checkbox"
                              checked={formData.serviceHours[option.key]}
                              onChange={() => handleServiceHoursChange(option.key)}
                              className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                            />
                            <span className="ml-2 text-sm text-gray-700">{option.label}</span>
                          </label>
                        ))}
                      </div>
                    </div>

                    {/* Delivery Modes */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700">
                        Delivery Modes *
                      </label>
                      <div className="mt-2 max-h-32 overflow-y-auto border border-gray-300 rounded-md p-2">
                        <div className="grid grid-cols-2 gap-2">
                          {deliveryModeOptions.map((mode) => (
                            <label key={mode} className="flex items-center">
                              <input
                                type="checkbox"
                                checked={formData.deliveryModes.includes(mode)}
                                onChange={() => handleDeliveryModesChange(mode)}
                                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                              />
                              <span className="ml-2 text-sm text-gray-700">{mode}</span>
                            </label>
                          ))}
                        </div>
                      </div>
                      {errors.deliveryModes && <p className="mt-1 text-sm text-red-600">{errors.deliveryModes}</p>}
                    </div>

                    {/* Locations Served */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700">
                        Locations Served
                      </label>
                      <div className="mt-2 max-h-32 overflow-y-auto border border-gray-300 rounded-md p-2">
                        {companies.map((company) => (
                          <label key={company.id} className="flex items-center mb-2">
                            <input
                              type="checkbox"
                              checked={formData.locationsServed.includes(company.id)}
                              onChange={() => handleLocationsServedChange(company.id)}
                              className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                            />
                            <span className="ml-2 text-sm text-gray-700">{company.name}</span>
                          </label>
                        ))}
                      </div>
                    </div>

                    {/* Company Legal Structure */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700">
                        Company Legal Structure *
                      </label>
                      <select
                        value={formData.companyLegalStructure}
                        onChange={(e) => handleInputChange('companyLegalStructure', e.target.value)}
                        className={`mt-1 block w-full border rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 ${
                          errors.companyLegalStructure ? 'border-red-300' : 'border-gray-300'
                        }`}
                      >
                        <option value="">Select Company Legal Structure</option>
                        {companyLegalStructureOptions.map((option) => (
                          <option key={option} value={option}>
                            {option}
                          </option>
                        ))}
                      </select>
                      {errors.companyLegalStructure && <p className="mt-1 text-sm text-red-600">{errors.companyLegalStructure}</p>}
                    </div>

                    {/* Business Insurance */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700">
                        Do you have valid Business Insurance? *
                      </label>
                      <select
                        value={formData.hasBusinessInsurance}
                        onChange={(e) => handleInputChange('hasBusinessInsurance', e.target.value)}
                        className={`mt-1 block w-full border rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 ${
                          errors.hasBusinessInsurance ? 'border-red-300' : 'border-gray-300'
                        }`}
                      >
                        <option value="">Select Business Insurance Status</option>
                        {businessInsuranceOptions.map((option) => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </select>
                      {errors.hasBusinessInsurance && <p className="mt-1 text-sm text-red-600">{errors.hasBusinessInsurance}</p>}
                    </div>

                    {/* Rating */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700">
                        Current Google Rating (0-5)
                      </label>
                      <input
                        type="number"
                        min="0"
                        max="5"
                        step="0.1"
                        value={formData.rating}
                        onChange={(e) => handleInputChange('rating', parseFloat(e.target.value) || 0)}
                        className={`mt-1 block w-full border rounded-md px-3 py-2 text-sm ${
                          errors.rating ? 'border-red-300' : 'border-gray-300'
                        } focus:outline-none focus:ring-blue-500 focus:border-blue-500`}
                        placeholder="Enter rating (0-5)"
                      />
                      {errors.rating && <p className="mt-1 text-sm text-red-600">{errors.rating}</p>}
                    </div>
                  </form>
                </div>

                {/* Drawer footer */}
                <div className="px-6 py-4 border-t border-gray-200">
                  <div className="flex justify-end space-x-3">
                    <button
                      type="button"
                      onClick={closeDrawer}
                      className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      onClick={saveVendor}
                      className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                    >
                      {editingVendor ? 'Update' : 'Save'}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Vendor Onboarding Form */}
      {showOnboardingForm && (
        <VendorOnboardingForm
          onClose={() => {
            setShowOnboardingForm(false);
            setEditingVendor(null);
          }}
          onSuccess={(vendorData) => {
            setShowOnboardingForm(false);
            setEditingVendor(null);
            // The vendor will be automatically added to the list via real-time updates
            if (editingVendor) {
              alert(`Vendor "${vendorData.businessName}" has been successfully updated!`);
            } else {
              alert(`Vendor "${vendorData.businessName}" has been successfully onboarded and approved!`);
            }
          }}
          editingVendor={editingVendor}
        />
      )}

      {/* Vendor Document Collection */}
      {showDocumentCollection && selectedVendorForDocs && (
        <VendorDocumentCollection
          vendor={selectedVendorForDocs}
          onClose={() => {
            setShowDocumentCollection(false);
            setSelectedVendorForDocs(null);
          }}
          onSuccess={(vendorData) => {
            setShowDocumentCollection(false);
            setSelectedVendorForDocs(null);
            alert(`Documents collected and vendor "${vendorData.name || vendorData.businessName}" has been approved!`);
          }}
        />
      )}
    </div>
  );
};

export default MealDeskVendorsManager; 